import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AdminTabsService } from '../../../../core/services/admin-tabs.service';
import { isAdminTabUrl } from '../../../../core/services/admin-tabs.utils';
import type { AdminSection } from '../../../../core/services/admin-tabs.utils';

/**
 * RESPONSIBILITY: open/close state for the admin section search picker
 *   (`<im-admin-search-picker>`). Hoisted out of the settings page so the
 *   global ⌘K pill in the app shell can drive it when the user is inside
 *   `/settings/*` — there's only ever one admin search affordance, not
 *   two competing ones.
 *
 * SIGNALS:
 *   anchor    — viewport coordinates the picker pins to. Right-aligned
 *               to the trigger (top + right offset from viewport edge)
 *               so the 360px-wide popover never spills off the right
 *               edge of the screen — the global search pill lives near
 *               the right of the glass top bar, so left-anchoring would
 *               always clip. Null when closed.
 *   isOpen    — convenience boolean derived from `anchor`.
 *
 * USED BY:
 *   - AppShellComponent.openCommandPalette() — calls open() when the URL
 *     is `/settings/*`, otherwise delegates to Command K (`im-cmdk`).
 *   - SettingsPageComponent — renders the picker; reads `anchor()` to
 *     position it; calls `close()` on dismiss/select.
 */
@Injectable({ providedIn: 'root' })
export class AdminSearchService {
  private readonly router = inject(Router);
  private readonly tabs = inject(AdminTabsService);

  private readonly _anchor = signal<{ top: number; right: number } | null>(null);

  readonly anchor = this._anchor.asReadonly();
  readonly isOpen = computed(() => this._anchor() !== null);

  /** Open the picker anchored just below the trigger element. Pass the
   *  source DOM element (the global search pill button) so the picker
   *  follows it; falls back to a safe corner when no element is given.
   *  Always right-aligns: `right` is measured from the viewport's right
   *  edge so the picker can never spill off the right of the screen. */
  open(trigger?: Element | null): void {
    if (!trigger) {
      this._anchor.set({ top: 64, right: 16 });
      return;
    }
    const rect = trigger.getBoundingClientRect();
    const right = Math.max(16, window.innerWidth - rect.right);
    this._anchor.set({ top: rect.bottom + 6, right });
  }

  /** Toggle the picker against the supplied trigger. */
  toggle(trigger?: Element | null): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open(trigger);
    }
  }

  close(): void {
    this._anchor.set(null);
  }

  /** Navigate to the chosen section using the admin tab service when the
   *  URL is tabbable, otherwise plain router navigation. */
  navigateTo(section: AdminSection): void {
    this.close();
    if (isAdminTabUrl(section.url)) {
      this.tabs.open(section.url, { newTab: false });
    } else {
      this.router.navigateByUrl(section.url);
    }
  }
}
